import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { generateCompliancePDF } from '../../../utils/roiPdfGenerator';

const ComplianceCalculator = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companySize: '',
    industry: '',
    dataVolume: '',
    geographicScope: [],
    regulations: [],
    currentCompliance: '',
    dataBreachHistory: '',
    contactInfo: {
      name: '',
      email: '',
      company: '',
      role: ''
    }
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const companySizeOptions = [
    { value: 'small', label: 'Small (< 250 employees)' },
    { value: 'medium', label: 'Medium (250-1000 employees)' },
    { value: 'large', label: 'Large (1000+ employees)' }
  ];

  const industryOptions = [
    { value: 'financial', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'technology', label: 'Technology' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'other', label: 'Other' }
  ];

  const dataVolumeOptions = [
    { value: 'low', label: 'Low (< 10,000 records)' },
    { value: 'medium', label: 'Medium (10,000 - 100,000 records)' },
    { value: 'high', label: 'High (100,000 - 1M records)' },
    { value: 'enterprise', label: 'Enterprise (1M+ records)' }
  ];

  const complianceOptions = [
    { value: 'none', label: 'No formal compliance program' },
    { value: 'basic', label: 'Basic policies and procedures' },
    { value: 'intermediate', label: 'Structured compliance framework' },
    { value: 'advanced', label: 'Comprehensive compliance program' }
  ];

  const regulationsList = [
    { id: 'gdpr', label: 'GDPR (General Data Protection Regulation)' },
    { id: 'aiact', label: 'EU AI Act' },
    { id: 'ccpa', label: 'CCPA (California Consumer Privacy Act)' },
    { id: 'hipaa', label: 'HIPAA (Healthcare)' },
    { id: 'pci', label: 'PCI DSS (Payment Card Industry)' },
    { id: 'sox', label: 'SOX (Sarbanes-Oxley)' }
  ];

  const geographicOptions = [
    { id: 'eu', label: 'European Union' },
    { id: 'us', label: 'United States' },
    { id: 'uk', label: 'United Kingdom' },
    { id: 'canada', label: 'Canada' },
    { id: 'apac', label: 'Asia-Pacific' },
    { id: 'global', label: 'Global Operations' }
  ];

  const calculateCompliance = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      // Base costs by company size
      const baseCosts = {
        small: 50000,
        medium: 150000,
        large: 500000
      };

      // Regulation multipliers
      const regulationCosts = {
        gdpr: 75000,
        aiact: 100000,
        ccpa: 50000,
        hipaa: 80000,
        pci: 60000,
        sox: 120000
      };

      // Penalty risks
      const penaltyRisks = {
        gdpr: 20000000, // 4% of annual turnover or €20M
        aiact: 35000000, // 7% of annual turnover or €35M
        ccpa: 7500, // $7,500 per violation
        hipaa: 1500000, // Up to $1.5M per incident
        pci: 100000, // $50-100k per month
        sox: 5000000 // Up to $5M
      };

      const baseImplementationCost = baseCosts?.[formData?.companySize] || baseCosts?.medium;
      
      let totalRegulationCosts = 0;
      let totalPenaltyRisk = 0;
      
      formData?.regulations?.forEach(reg => {
        totalRegulationCosts += regulationCosts?.[reg] || 0;
        totalPenaltyRisk += penaltyRisks?.[reg] || 0;
      });

      // Geographic complexity multiplier
      const geoMultiplier = 1 + (formData?.geographicScope?.length * 0.2);
      
      // Data volume multiplier
      const volumeMultipliers = {
        low: 1,
        medium: 1.3,
        high: 1.6,
        enterprise: 2.0
      };
      
      const volumeMultiplier = volumeMultipliers?.[formData?.dataVolume] || 1;
      
      const totalImplementationCost = (baseImplementationCost + totalRegulationCosts) * geoMultiplier * volumeMultiplier;
      const annualMaintenanceCost = totalImplementationCost * 0.3; // 30% of implementation
      const potentialPenalties = totalPenaltyRisk * 0.1; // 10% risk probability
      
      const totalFirstYearCost = totalImplementationCost + annualMaintenanceCost;
      const costAvoidance = potentialPenalties + (totalImplementationCost * 0.5); // Efficiency gains
      const netBenefit = costAvoidance - totalFirstYearCost;

      setResults({
        implementationCost: totalImplementationCost,
        annualMaintenance: annualMaintenanceCost,
        totalFirstYear: totalFirstYearCost,
        potentialPenalties: potentialPenalties,
        costAvoidance: costAvoidance,
        netBenefit: netBenefit,
        roi: ((costAvoidance - totalFirstYearCost) / totalFirstYearCost) * 100,
        paybackPeriod: totalFirstYearCost / (costAvoidance / 12),
        riskReduction: (potentialPenalties / totalPenaltyRisk) * 100
      });
      
      setIsCalculating(false);
      setCurrentStep(4);
    }, 2000);
  };

  const handleInputChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleCheckboxChange = (field, itemId, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev?.[field], itemId]
        : prev?.[field]?.filter(id => id !== itemId)
    }));
  };

  const handleDownloadReport = () => {
    generateCompliancePDF(results, formData);
    if (onComplete) {
      onComplete(results);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Company Profile</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Company Size"
                  options={companySizeOptions}
                  value={formData?.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                  required
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  required
                />
                <Select
                  label="Data Volume"
                  options={dataVolumeOptions}
                  value={formData?.dataVolume}
                  onChange={(value) => handleInputChange('dataVolume', value)}
                  required
                />
                <Select
                  label="Current Compliance Level"
                  options={complianceOptions}
                  value={formData?.currentCompliance}
                  onChange={(value) => handleInputChange('currentCompliance', value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Regulatory Requirements</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Which regulations apply to your business? (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {regulationsList?.map((regulation) => (
                      <Checkbox
                        key={regulation?.id}
                        label={regulation?.label}
                        checked={formData?.regulations?.includes(regulation?.id)}
                        onChange={(e) => handleCheckboxChange('regulations', regulation?.id, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Geographic Scope (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {geographicOptions?.map((geo) => (
                      <Checkbox
                        key={geo?.id}
                        label={geo?.label}
                        checked={formData?.geographicScope?.includes(geo?.id)}
                        onChange={(e) => handleCheckboxChange('geographicScope', geo?.id, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Contact Information</h3>
              <p className="text-muted-foreground mb-6">
                Get your personalized compliance cost analysis report.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="Jane Smith"
                  value={formData?.contactInfo?.name}
                  onChange={(e) => handleInputChange('contactInfo.name', e?.target?.value)}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="jane@company.com"
                  value={formData?.contactInfo?.email}
                  onChange={(e) => handleInputChange('contactInfo.email', e?.target?.value)}
                  required
                />
                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Tech Solutions Ltd"
                  value={formData?.contactInfo?.company}
                  onChange={(e) => handleInputChange('contactInfo.company', e?.target?.value)}
                  required
                />
                <Input
                  label="Job Title"
                  type="text"
                  placeholder="Compliance Officer"
                  value={formData?.contactInfo?.role}
                  onChange={(e) => handleInputChange('contactInfo.role', e?.target?.value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {isCalculating ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-primary mb-2">Calculating Compliance Costs...</h3>
                <p className="text-muted-foreground">
                  Analyzing regulatory requirements and potential penalties
                </p>
              </div>
            ) : results && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-6">Your Compliance Cost Analysis</h3>
                
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-error/10 to-error/5 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-error mb-2">
                      {formatCurrency(results?.potentialPenalties)}
                    </div>
                    <div className="text-sm text-muted-foreground">Potential Penalties</div>
                  </div>
                  <div className="bg-gradient-to-br from-warning/10 to-warning/5 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-warning mb-2">
                      {formatCurrency(results?.totalFirstYear)}
                    </div>
                    <div className="text-sm text-muted-foreground">First Year Investment</div>
                  </div>
                  <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-success mb-2">
                      {results?.roi > 0 ? '+' : ''}{results?.roi?.toFixed(0)}%
                    </div>
                    <div className="text-sm text-muted-foreground">ROI</div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-surface rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-primary mb-4">Cost Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Implementation Cost</span>
                      <span className="font-medium">{formatCurrency(results?.implementationCost)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Annual Maintenance</span>
                      <span className="font-medium">{formatCurrency(results?.annualMaintenance)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between items-center font-semibold">
                      <span>Total First Year Cost</span>
                      <span className="text-warning">{formatCurrency(results?.totalFirstYear)}</span>
                    </div>
                  </div>
                </div>

                {/* Risk Analysis */}
                <div className="bg-white border border-error/20 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-primary mb-4">Risk Analysis</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Potential Annual Penalties</span>
                      <span className="font-medium text-error">{formatCurrency(results?.potentialPenalties)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Risk Reduction</span>
                      <span className="font-medium text-success">{results?.riskReduction?.toFixed(0)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Payback Period</span>
                      <span className="font-medium">{results?.paybackPeriod?.toFixed(1)} months</span>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-white border border-secondary/20 rounded-lg p-6">
                  <h4 className="font-semibold text-primary mb-3">Recommended Actions</h4>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                      <span className="text-sm">Conduct comprehensive compliance gap analysis</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Shield" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Implement data governance framework</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="FileText" size={16} color="var(--color-accent)" />
                      <span className="text-sm">Develop compliance monitoring and reporting system</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="default"
                      iconName="Download"
                      iconPosition="left"
                      className="bg-secondary hover:bg-secondary/90"
                      onClick={handleDownloadReport}
                    >
                      Download Report
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Book Consultation
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-primary">Compliance Cost Calculator</h2>
            <p className="text-sm text-muted-foreground">
              Estimate your regulatory compliance investment and risk exposure
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-surface">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-primary">
              Step {currentStep} of 4
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / 4) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div 
              className="bg-secondary h-2 rounded-full transition-all duration-300 progress-bar"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface">
          <Button
            variant="outline"
            onClick={prevStep}
            iconName="ChevronLeft"
            iconPosition="left"
            className="hover:scale-105 hover:shadow-md focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
          >
            Previous
          </Button>
          
          <div className="flex space-x-2">
            {[1, 2, 3, 4]?.map((step) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  step === currentStep ? 'bg-secondary' : 
                  step < currentStep ? 'bg-secondary/50' : 'bg-border'
                }`}
              />
            ))}
          </div>

          {currentStep < 3 ? (
            <Button
              variant="default"
              onClick={nextStep}
              iconName="ChevronRight"
              iconPosition="right"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
            >
              Next Step
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={calculateCompliance}
              loading={isCalculating}
              iconName="Shield"
              iconPosition="right"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
            >
              Calculate Compliance
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceCalculator;